import { useState } from 'react';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { createSha256 } from '@ibk/shared-utils';
import { ApiErrors } from '../../enums';
import { usePostSchedulePixMutation } from '../../services/pixApi';
import { handlePixError } from '../../utils/pixErrorHandler';
const ORIGIN_BANK_NAME = 'CELCOIN IP S.A.';
const ERRORS_MESSAGES = {
    [ApiErrors.CAN_NOT_SCHEDULE_PIX]: 'error-to-schedule-the-pix-try-again-later',
    [ApiErrors.NO_FUNDS_AVAILABLE]: 'insufficient-balance-modal-title',
    [ApiErrors.ERROR_NO_FUNDS_AVAILABLE]: 'insufficient-balance-modal-title',
    [ApiErrors.PIN_FAILURE_TO_VALIDATE]: 'pin-failure-to-validate-error',
    [ApiErrors.TIMEOUT_ERROR]: 'pix-timeout-error',
    [ApiErrors.PIX_LIMIT_EXCEEDED]: 'pix-limit-exceeded',
};
export function usePostSchedulePix() {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed, cache } = useFeatureFlag(undefined, {
        autoLoad: false,
    });
    const [trigger] = usePostSchedulePixMutation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    const [pixScheduled, setPixScheduled] = useState();
    async function postSchedulePix(body, startAt, rethrow) {
        setLoading(true);
        setError(undefined);
        setPixScheduled(undefined);
        try {
            const usePermissions = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAYMENTS_OPERATOR_REQUEST_PAYMENT, undefined, cache?.data);
            const transactionIdempotencyId = await createSha256({
                transactionAt: startAt,
                accountId: getSelectedAccountInfo()?.accountId,
                amount: body.amount,
                key: body.pixDetails.key,
                account: body.pixDetails.counterpartyBankAccount,
                branch: body.pixDetails.counterpartyBankBranch,
                code: body.pixDetails.counterpartyBankCode,
                pin: body.credential,
                accountPayablesId: body.accountPayablesId,
            });
            const requestBody = {
                ...body,
                pixDetails: {
                    ...body.pixDetails,
                    originBankName: ORIGIN_BANK_NAME,
                    originName: getSelectedAccountInfo()?.companyName ??
                        `${getSelectedAccountInfo()?.firstName} ${getSelectedAccountInfo()?.lastName}`,
                    originDocument: getSelectedAccountInfo()?.documentNumber,
                },
            };
            const { result } = await trigger({
                body: requestBody,
                transactionIdempotencyId,
                usePermissions,
            }).unwrap();
            setPixScheduled(result);
            return result;
        }
        catch (err) {
            const message = handlePixError({
                error: err,
                translate: t,
                errorMessages: ERRORS_MESSAGES,
                defaultErrorKey: ApiErrors.CAN_NOT_SCHEDULE_PIX,
            });
            setError(message);
            if (rethrow) {
                throw new Error(message);
            }
        }
        finally {
            setLoading(false);
        }
    }
    return {
        loading,
        setLoading,
        error,
        setError,
        pixScheduled,
        setPixScheduled,
        postSchedulePix,
    };
}
